<template>
  <div
    id="search-results"
    class="overflow-y-auto overscroll-none border border-black-10"
  >
    <button
      v-if="showClose"
      class="absolute right-3 top-3 px-2.5 py-1.5"
      :aria-label="$t('dialog.close')"
      @click="closeButtonClicked"
    >
      <Icon class="size-5" icon="close" />
    </button>
    <div v-if="isFetching" class="flex flex-col pt-5">
      <div class="mr-8 flex flex-1 flex-col gap-2 px-4 pb-4">
        <Skeleton
          v-for="n in 5"
          :key="n"
          class="h-8 w-full"
          :index="n"
          type="custom"
        />
      </div>
    </div>
    <template v-else>
      <div class="flex flex-col">
        <div
          v-if="categories.length || brands.length"
          class="flex-1"
          :class="{
            'border-b border-black-20 pb-4': productSuggestions.length,
          }"
        >
          <SearchResultGroup
            v-if="categories.length"
            :categories="categories.slice(0, CATEGORY_LIMIT)"
            :search-term="searchTerm"
            @click:result="$emit('click:result')"
          />
        </div>
        <div v-if="productSuggestions.length" class="flex-1">
          <SearchResultGroup
            :products="productSuggestions.slice(0, PRODUCT_LIMIT)"
            :search-term="searchTerm"
            :show-images="true"
            @click:result="$emit('click:result')"
          />
        </div>
      </div>

      <hr v-if="resultsCount > 0" class="mt-3 h-px border-0 bg-black-20" />

      <div v-if="resultsCount > 0" class="pb-16 text-left md:pb-0">
        <NuxtLink
          class="mb-20 flex items-center px-5 py-4 transition-colors duration-100 ease-in-out hover:bg-black-10 sm:mb-0"
          :to="toLocalePath({ name: 'search', query: { term: searchTerm } })"
          @click.prevent="$emit('click:view-all')"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="$t('search.search_results')" />
          <IconArrowRight class="ml-1 inline-block size-2.5" />
        </NuxtLink>
      </div>

      <section v-else-if="hasNoResults" class="px-8 py-5 text-center text-base">
        <p>{{ $t('search.no_result') }}</p>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  PRODUCT_LIMIT,
  CATEGORY_LIMIT,
  MIN_CHARS_FOR_SEARCH,
} from '../../constants/search'

const props = defineProps({
  fetching: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showClose: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  brands: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  productSuggestions: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  categories: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  searchTerm: {
    type: String as PropType<string>,
    default: '',
  },
  resultsCount: {
    type: Number as PropType<number>,
    default: 0,
  },
})
const emit = defineEmits(['onFlyoutDismiss', 'click:result', 'click:view-all'])

const { toLocalePath } = useRouteHelpers()

const hasNoResults = computed(
  () =>
    props.resultsCount <= 0 &&
    props.searchTerm.length >= MIN_CHARS_FOR_SEARCH &&
    !props.fetching,
)

const isFetching = computed(() => props.fetching && props.resultsCount <= 0)
const closeButtonClicked = () => emit('onFlyoutDismiss')
</script>
